import { useState } from "react";
import { Search, Filter, Bell } from "lucide-react"; // Removed Check
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { notificationsApi } from "@/services/notifications";
import { useAuthStore } from "@/stores/auth";
import { usePostsStore } from "@/stores/posts";
import { Avatar, AvatarImage } from "@/components/ui/avatar";
import { SearchOverlay } from "./SearchOverlay";
import { FilterModal } from "./FilterModal"; // Import the new FilterModal

// Default filter states, can be shared or defined here if specific to this navbar's filter usage
const defaultSelectedFilters = {
  forYou: true,
  recent: false,
  nearby: false,
  trending: false,
};

const defaultSelectedTags = {
  all: true,
  need: false,
  borrow: false,
  give: false,
  alert: false,
  event: false,
  share: false,
};

export function AuthenticatedMobileNavbar() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  // State to store the last saved/applied filters to pass to the modal
  const [currentAppliedFilters, setCurrentAppliedFilters] = useState(
    defaultSelectedFilters,
  );
  const [currentAppliedTags, setCurrentAppliedTags] =
    useState(defaultSelectedTags);

  const { data: unreadData } = useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: notificationsApi.getUnreadCount,
    refetchInterval: 60000,
  });
  const unreadCount = unreadData?.unread_count || 0;

  const handleSaveFiltersFromModal = (
    newFilters: typeof defaultSelectedFilters,
    newTags: typeof defaultSelectedTags,
  ) => {
    const { setOrdering, setSearch, setCategory, fetchPosts } =
      usePostsStore.getState();
    let ordering: string | undefined = undefined;
    let categoryFilter: string | string[] | undefined = undefined;

    if (newFilters.recent) ordering = "-created_at";
    else if (newFilters.trending) ordering = "-likes_count";
    // 'forYou' and 'nearby' might require different logic or backend support

    const categoryTags = Object.entries(newTags)
      .filter(([key, value]) => value && key !== "all")
      .map(([key]) => {
        switch (key) {
          case "need":
            return "help";
          case "borrow":
            return "alert";
          case "give":
            return "give";
          case "alert":
            return "warning";
          case "event":
            return "event";
          case "share":
            return "share";
          default:
            return null;
        }
      })
      .filter(Boolean) as string[];

    if (categoryTags.length > 1) categoryFilter = categoryTags;
    else if (categoryTags.length === 1) categoryFilter = categoryTags[0];

    setOrdering(ordering);
    setCategory(categoryFilter);
    setSearch(undefined); // Assuming general search is cleared when applying filters

    fetchPosts(1);

    // Update the state that holds the currently applied filters
    setCurrentAppliedFilters(newFilters);
    setCurrentAppliedTags(newTags);
    setShowFilterModal(false); // Close modal is handled by FilterModal's onSave
  };

  return (
    <>
      <div className="w-full bg-[#FBF6F1] border-b border-gray-200 py-2">
        <div className="px-4 py-2 flex items-center gap-2 w-full">
          <Link to="/profile" className="flex items-center">
            <Avatar className="h-8 w-8">
              <AvatarImage
                src={user?.avatar_url || "/avatars/avatar.svg"}
                alt={user?.display_name || "User"}
              />
            </Avatar>
          </Link>
          <div
            className="flex-1 flex items-center bg-[#F7F7F7] rounded-full px-3 py-1.5 mx-1 cursor-pointer"
            onClick={() => setShowSearch(true)}
          >
            <Search className="h-4 w-4 text-[#6F6F6B] mr-2" />
            <input
              type="text"
              placeholder="Tìm kiếm"
              className="bg-transparent border-none outline-none text-sm w-full text-[#6F6F6B] font-normal font-sora"
              readOnly
            />
          </div>
          <button className="p-1.5" onClick={() => setShowFilterModal(true)}>
            <Filter className="h-5 w-5 text-[#1E3A3A]" />
          </button>
          <button
            className="p-1.5 relative"
            onClick={() => navigate("/notifications")}
          >
            <Bell className="h-5 w-5 text-[#1E3A3A]" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white bg-red-500 rounded-full">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {showSearch && <SearchOverlay onClose={() => setShowSearch(false)} />}

      <FilterModal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        initialSelectedFilters={currentAppliedFilters}
        initialSelectedTags={currentAppliedTags}
        onSaveFilters={handleSaveFiltersFromModal}
      />
    </>
  );
}
